import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Dialog, DialogProps } from '../../ui';
import { ExitIcon } from '../../icons';
import { InlineSpinner } from '../../common/spinner';
import { useHistory } from 'react-router-dom';
import { useMediaQuery } from '../../../hooks/utils/useMediaQuery';
import { useSettingsModal, SettingsPage } from '../../../contexts/settings-modal-context';
import { Navigation } from './navigation';
import { UnsavedChangesModal } from '../unsaved-changes-modal';
import useAuth from '../../../hooks/utils/useAuth';
import Toast from '../../common/pop-up-message';

// Import page components
import { Profile } from '../../../containers/settings/profile';
import { Notifications } from '../../../containers/settings/notifications';
import { InviteDependents } from '../../../containers/settings/invite-dependents';
import { ChangePassword } from '../../../containers/settings/change-password';
import { MyPlan } from '../../../containers/settings/my-plan';
import { PaymentMethod } from '../../../containers/settings/payment-method';
import { CallHistory } from '../../../containers/settings/call-history';
import { MyFavorites } from '../../../containers/settings/my-favorites';
import { AboutUs } from '../../../containers/settings/about-us';
import { PrivacyPolicy } from '../../../containers/settings/privacy-policy';
import { TermsOfUse } from '../../../containers/settings/terms-of-use';
import { ROUTE_PATH } from '../../../routes/route-paths';
import { pageToTabParam } from '../../../utils/settings-utils';
import { takeKcActionStatus } from '../../../utils/stale-oauth-params';

export interface SettingsModalProps extends Omit<DialogProps, 'open' | 'onClose'> {}

export const SettingsModal: React.FC<SettingsModalProps> = ({ ...rest }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const history = useHistory();
  const { isLoading: userIsLoading } = useAuth();
  const isDesktop = useMediaQuery('md');
  const { isModalOpen, selectedPage, closeModal } = useSettingsModal();

  const [currentPage, setCurrentPage] = useState<SettingsPage | undefined>(selectedPage);
  const [nextPage, setNextPage] = useState<SettingsPage | undefined>();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [profileSaveHandler, setProfileSaveHandler] = useState<(() => Promise<void>) | null>(null);

  // Memoize the callback to prevent infinite loops
  const handleSaveRequestReady = useCallback((handler: () => Promise<void>) => {
    if (typeof handler === 'function') {
      setProfileSaveHandler(() => handler);
    }
  }, []);

  // Track the previous selected page to detect page changes
  const prevSelectedPageRef = useRef<SettingsPage | undefined>(selectedPage);

  useEffect(() => {
    const isPageChange = prevSelectedPageRef.current !== selectedPage;
    setCurrentPage(selectedPage || SettingsPage.myProfile);

    // Only reset unsaved changes when navigating between different pages
    // Don't reset profileSaveHandler here - let the child components set it when they mount
    if (isPageChange) {
      setHasUnsavedChanges(false);
    }

    prevSelectedPageRef.current = selectedPage;
  }, [selectedPage]);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [currentPage]);

  // Redirect mobile users (below md breakpoint) to appropriate settings page instead of showing modal
  useEffect(() => {
    if (!isDesktop && isModalOpen) {
      const targetPage = selectedPage || SettingsPage.myProfile;
      closeModal();

      if (
        targetPage === SettingsPage.myProfile ||
        targetPage === SettingsPage.notifications ||
        targetPage === SettingsPage.changePassword ||
        targetPage === SettingsPage.inviteDependents
      ) {
        history.push(ROUTE_PATH.SETTINGS_PROFILE);
      } else if (
        targetPage === SettingsPage.myPlan ||
        targetPage === SettingsPage.paymentMethod ||
        targetPage === SettingsPage.callHistory
      ) {
        const tabParam = pageToTabParam(targetPage);
        history.push(`${ROUTE_PATH.SETTINGS_MEMBERSHIP}?tab=${tabParam}`);
      } else if (
        targetPage === SettingsPage.aboutUs ||
        targetPage === SettingsPage.privacyPolicy ||
        targetPage === SettingsPage.termsOfUse
      ) {
        history.push(ROUTE_PATH.SETTINGS_INFO);
      } else {
        history.push(ROUTE_PATH.SETTINGS_PROFILE);
      }
    }
  }, [isDesktop, isModalOpen, closeModal, selectedPage, history]);

  // auth-context strips kc_action_status from the URL before Keycloak init
  useEffect(() => {
    if (takeKcActionStatus() === 'success') {
      Toast.success('Password updated successfully!');
    }
  }, []);

  const handlePageSelect = useCallback((page: SettingsPage) => {
    setCurrentPage(page);
  }, []);

  const handleUnsavedChangesWarning = useCallback((page: SettingsPage) => {
    setShowUnsavedChangesModal(true);
    setNextPage(page);
  }, []);

  const onCloseModalTab = useCallback(() => {
    setShowUnsavedChangesModal(false);
    setHasUnsavedChanges(false);
    if (nextPage) {
      setCurrentPage(nextPage);
      setNextPage(undefined);
    } else {
      closeModal();
    }
  }, [nextPage, closeModal]);

  // Callback for when save is successful from the unsaved changes modal
  const onSaveFromModalSuccess = useCallback(() => {
    setShowUnsavedChangesModal(false);
    setHasUnsavedChanges(false);
    // Navigate to the page they were trying to go to
    if (nextPage) {
      setCurrentPage(nextPage);
      setNextPage(undefined);
    }
  }, [nextPage]);

  const onCloseModal = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowUnsavedChangesModal(true);
    } else {
      closeModal();
    }
  }, [hasUnsavedChanges, closeModal]);

  const renderPage = () => {
    const page = currentPage || SettingsPage.myProfile;
    switch (page) {
      case SettingsPage.myProfile:
        return (
          <Profile
            showUnsavedChangesModal={showUnsavedChangesModal}
            onChange={() => setHasUnsavedChanges(true)}
            onCloseUnsavedChangesModal={() => setShowUnsavedChangesModal(false)}
            onDiscardUnsavedChangesModal={onCloseModalTab}
            onSuccessSubmitted={undefined}
            onSubmittingChange={setIsSubmitting}
            onSaveRequestReady={handleSaveRequestReady}
            onSaveSuccess={() => setHasUnsavedChanges(false)}
          />
        );
      case SettingsPage.notifications:
        return <Notifications onChange={() => setHasUnsavedChanges(true)} onSubmittingChange={setIsSubmitting} />;
      case SettingsPage.inviteDependents:
        return <InviteDependents />;
      case SettingsPage.changePassword:
        return <ChangePassword />;
      case SettingsPage.myPlan:
        return <MyPlan />;
      case SettingsPage.paymentMethod:
        return <PaymentMethod />;
      case SettingsPage.callHistory:
        return <CallHistory />;
      case SettingsPage.myFavorites:
        return <MyFavorites />;
      case SettingsPage.aboutUs:
        return <AboutUs />;
      case SettingsPage.privacyPolicy:
        return <PrivacyPolicy />;
      case SettingsPage.termsOfUse:
        return <TermsOfUse />;
      default:
        return <Profile onChange={() => setHasUnsavedChanges(true)} onSubmittingChange={setIsSubmitting} />;
    }
  };

  return (
    <Dialog
      className="h-150 shadow-modal bg-whiteSmoke backdrop:bg-modalBackdropColor w-full max-w-5xl rounded-lg p-0"
      contentClassName="flex h-full"
      open={isModalOpen}
      onClose={onCloseModal}
      {...rest}
    >
      {userIsLoading ? (
        <div className="flex h-full w-full items-center justify-center">
          <InlineSpinner />
        </div>
      ) : (
        <>
          <div className="flex h-full w-1/3 flex-col border-r-2 border-neutral-200">
            <div className="flex items-center justify-end border-b border-neutral-200 p-4">
              <button className="" onClick={onCloseModal}>
                <ExitIcon color="#22282F" />
              </button>
            </div>
            <Navigation
              selectedPage={currentPage}
              onPageSelect={handlePageSelect}
              hasUnsavedChanges={hasUnsavedChanges}
              onUnsavedChangesWarning={handleUnsavedChangesWarning}
            />
          </div>
          <div
            ref={containerRef}
            className={`max-h-full w-2/3 overflow-auto ${currentPage !== SettingsPage.myProfile && 'p-6'}`}
          >
            {renderPage()}
          </div>
          <UnsavedChangesModal
            open={showUnsavedChangesModal}
            onClose={() => {
              setShowUnsavedChangesModal(false);
            }}
            onDiscard={onCloseModalTab}
            onSave={async () => {
              if (profileSaveHandler && typeof profileSaveHandler === 'function') {
                try {
                  await profileSaveHandler();
                  // After successful save from modal, navigate to the target page
                  onSaveFromModalSuccess();
                } catch (error) {
                  // Error handling - the handler should handle its own errors
                }
              }
            }}
            savingInProgress={isSubmitting}
          />
        </>
      )}
    </Dialog>
  );
};
